import classNames from "classnames";
import { ReactNode } from "react";

import {
  LaidOutSegment,
  MeterSegment,
  MeterTone,
  ShareBucket,
  layoutSegments,
  segmentStyle,
} from "./segments";

export type { MeterSegment, MeterTone, ShareBucket } from "./segments";

const TRACK_TONE: Record<MeterTone, string> = {
  positive: "bg-success",
  info: "bg-primary",
  neutral: "bg-raised",
  caution: "bg-warning",
};

export interface SegmentedMeterProps {
  /** Parts of the whole, in render order. Segments worth nothing are dropped. */
  segments: MeterSegment[];
  /** The denominator. At or below zero the meter renders an inert track. */
  total: number;
  /** Translated sentence naming the denominator, e.g. "of $2,617.50 invoiced". */
  totalLabel: string;
  /** Translated sentence shown in place of the legend when there is nothing to show. */
  emptyMessage: string;
  /** Set false when a share of `total` would not be an honest share. Defaults to true. */
  showShares?: boolean;
  /** Renders a share; receives the bucket so a clamped share can be worded differently. */
  formatShare?: (percent: number, bucket: ShareBucket) => string;
  /** Optional line below the legend, for a state the bar itself must not imply. */
  note?: ReactNode;
  /** Track thickness. Defaults to "md" (10px). */
  height?: "sm" | "md";
}

function defaultFormatShare(percent: number, bucket: ShareBucket): string {
  if (bucket === "under_one") return "(<1%)";
  if (bucket === "over_ninety_nine") return "(>99%)";
  return `(${percent}%)`;
}

/**
 * A single track divided into named parts, with a legend that states each part
 * in words and figures.
 *
 * The component owns the rules that keep the bar honest, so that no call site
 * has to restate them: a segment worth nothing is never drawn or listed, a
 * non-zero segment is never too narrow to see, and a share never rounds into a
 * claim that is untrue at the edges. The track is decorative and hidden from
 * assistive technology — the legend is the content, so nothing here is carried
 * by colour alone. Callers supply tones rather than colours, and every string is
 * injected.
 */
export function SegmentedMeter({
  segments,
  total,
  totalLabel,
  emptyMessage,
  showShares = true,
  formatShare = defaultFormatShare,
  note,
  height = "md",
}: SegmentedMeterProps) {
  const laidOut = layoutSegments(segments, total);
  const isEmpty = laidOut.length === 0;

  return (
    <div className="font-sans w-full">
      <div
        aria-hidden="true"
        data-testid="segmented-meter-track"
        className={classNames(
          "flex gap-[2px] w-full rounded-full bg-surface overflow-hidden",
          height === "sm" ? "h-1.5" : "h-2.5",
        )}
      >
        {laidOut.map((segment) => (
          <span
            key={segment.key}
            style={segmentStyle(segment)}
            className={classNames("h-full", TRACK_TONE[segment.tone])}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-baseline gap-x-[18px] gap-y-1.5 mt-2.5 text-[0.78125rem] leading-snug text-muted">
        {isEmpty ? (
          <span>{emptyMessage}</span>
        ) : (
          <>
            {laidOut.map((segment) => (
              <LegendItem
                key={segment.key}
                segment={segment}
                share={
                  showShares
                    ? formatShare(segment.percent, segment.bucket)
                    : null
                }
              />
            ))}
            <span className="ml-auto">{totalLabel}</span>
          </>
        )}
      </div>

      {note}
    </div>
  );
}

function LegendItem({
  segment,
  share,
}: {
  segment: LaidOutSegment;
  share: string | null;
}) {
  return (
    <span className="inline-flex items-baseline gap-[7px]">
      <span
        aria-hidden="true"
        className={classNames(
          "w-2 h-2 shrink-0 rounded-full -translate-y-px",
          TRACK_TONE[segment.tone],
        )}
      />
      <span>{segment.label}</span>
      <span className="font-semibold tabular-nums text-default">
        {segment.formattedValue}
      </span>
      {share !== null && <span>{share}</span>}
    </span>
  );
}

export default SegmentedMeter;
